import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Mail, Clock, Calendar } from "lucide-react";
import { Skeleton } from "../ui/skeleton";
import { ppcFarmService } from "../../services/ppcFarmService";

interface PpcFarmMailingMetricsBarProps {
  loading: boolean;
  error?: string | null;
}

const PpcFarmMailingMetricsBar: React.FC<PpcFarmMailingMetricsBarProps> = ({
  loading,
  error,
}) => {
  const [metrics, setMetrics] = useState({
    mailersSent: 0,
    pendingShipment: 0,
    lastUpdated: null as Date | null,
  });
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Fetch metrics from backend metadata
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setMetricsLoading(true);
        const statistics = await ppcFarmService.getLedgerStatistics(
          "mailing" as any,
        );

        // Get the pre-calculated values from backend
        const mailingStats = statistics as any;

        setMetrics({
          mailersSent: mailingStats.mailersSent || 0,
          pendingShipment: mailingStats.mailersPending || 0,
          lastUpdated: mailingStats.lastUpdated
            ? mailingStats.lastUpdated.toDate()
            : null,
        });
      } catch (error) {
        console.error("Error fetching mailing metrics:", error);
        // Fallback to zero values
        setMetrics({
          mailersSent: 0,
          pendingShipment: 0,
          lastUpdated: null,
        });
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const formatDate = (date: Date | null): string => {
    if (!date) return "Never";

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <Mail className="h-4 w-4" />
            <span className="text-sm font-medium">
              Error loading metrics: {error}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const metricsData = [
    {
      label: "Mailers Sent",
      value: metrics.mailersSent.toLocaleString(),
      icon: Mail,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      label: "Pending Shipment",
      value: metrics.pendingShipment.toLocaleString(),
      icon: Clock,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
    },
    {
      label: "Last Updated",
      value: formatDate(metrics.lastUpdated),
      icon: Calendar,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {metricsData.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-3 flex items-center justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <IconComponent className={`h-4 w-4 ${metric.color} mr-2`} />
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {metric.label}
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {metric.value}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PpcFarmMailingMetricsBar;
